import type { CSSProperties, FC } from "react";
import { useContext, useEffect, useMemo, useRef } from "react";
import { FormProvider, useForm, useWatch, type FieldErrors } from "react-hook-form";
import { Card, FlexContent, Tabs, type Tab } from "@rothoblaas/design-system";
import { resolveAccentColor } from "../../../constants/colors";
import NotificationDispatch, { showErrorNotification } from "../../../context/notificationContext";
import useI18n from "../../../hooks/useTranslations";
import { computeBeamReinforcement, type BeamReinforcementResult } from "../../../http/beamReinforcement/formulas";
import { type BeamReinforcementConfiguration } from "../../../http/beamReinforcement/types/configuration";
import { Screw } from "../../../http/beamReinforcement/types/screw";
import { TimberClass } from "../../../http/beamReinforcement/types/timberClass";
import {
  buildBeamReinforcementConfigurationDefaults,
  useAutoSaveBeamReinforcementConfiguration,
  type BeamReinforcementConfigurationFormValues,
} from "../../../http/beamReinforcement/useAutoSaveBeamReinforcementConfiguration";
import useBeamReinforcementScrewsApi from "../../../http/beamReinforcement/useScrews";
import useBeamReinforcementTimbersApi from "../../../http/beamReinforcement/useTimbers";
import type { Calculation } from "../../../http/types/calculations";
import styles from "./BeamReinforcement.module.css";
import BeamsCard from "./BeamsCard";
import GeneralDataCard from "./GeneralDataCard";
import ResultsCard from "./ResultsCard";
import ScrewCard from "./ScrewCard";
import SectionDrawingCard from "./SectionDrawingCard";

export type BeamReinforcementProps = {
  data?: Calculation<BeamReinforcementConfiguration>;
  isAdmin?: boolean;
  readOnly?: boolean;
};

type BeamReinforcementFieldName = keyof BeamReinforcementConfigurationFormValues;

const BEAM_REINFORCEMENT_TAB_PARAMETER = "beamReinforcement_tab";

const GENERAL_TAB_FIELDS = [
  "serviceClass",
  "loadDuration",
  "tipTolerance",
  "permanentLoad",
  "liveLoad",
  "beamLength",
] as const satisfies readonly BeamReinforcementFieldName[];

const BEAMS_TAB_FIELDS = [
  "existingMaterial",
  "existingBase",
  "existingHeight",
  "existingLength",
  "reinforcementMaterial",
  "reinforcementBase",
  "reinforcementHeight",
  "reinforcementLength",
  "reinforcementSides",
] as const satisfies readonly BeamReinforcementFieldName[];

const SCREW_TAB_FIELDS = [
  "screw",
  "screwSpacing",
  "preDrilled",
] as const satisfies readonly BeamReinforcementFieldName[];

const hasTabValidationError = (
  fields: readonly BeamReinforcementFieldName[],
  errors: FieldErrors<BeamReinforcementConfigurationFormValues>,
): boolean => fields.some((fieldName) => !!errors[fieldName]);

const BeamReinforcement: FC<BeamReinforcementProps> = ({ data, readOnly = false }) => {
  const i18n = useI18n();
  const dispatch = useContext(NotificationDispatch);
  const screwsApi = useBeamReinforcementScrewsApi();
  const timbersApi = useBeamReinforcementTimbersApi();
  const screwsAborter = useRef(new AbortController());
  const timbersAborter = useRef(new AbortController());

  const defaultValues = useMemo<BeamReinforcementConfigurationFormValues>(
    () => buildBeamReinforcementConfigurationDefaults(data),
    [data],
  );

  const formMethods = useForm<BeamReinforcementConfigurationFormValues>({
    defaultValues,
    mode: "onChange",
  });

  useEffect(() => {
    formMethods.reset(buildBeamReinforcementConfigurationDefaults(data));
  }, [data, formMethods]);

  useAutoSaveBeamReinforcementConfiguration(data, formMethods.control);

  const cardDefaultValues = useMemo<BeamReinforcementConfigurationFormValues>(
    () => ({ ...defaultValues }),
    [defaultValues],
  );

  const accentColor = resolveAccentColor(data?.module?.category.color);
  const watchedValues = useWatch({ control: formMethods.control });
  const screwsSwr = screwsApi.getBeamReinforcementScrews(screwsAborter.current.signal);
  const timbersSwr = timbersApi.getBeamReinforcementTimbers(timbersAborter.current.signal);
  const screws = screwsSwr.data ?? [];
  const timbers = timbersSwr.data ?? [];

  useEffect(() => {
    void screwsSwr.trigger().catch((err) => dispatch(showErrorNotification(i18n, err)));
    void timbersSwr.trigger().catch((err) => dispatch(showErrorNotification(i18n, err)));
  }, []);

  const result = useMemo<BeamReinforcementResult | null>(() => {
    if (
      watchedValues.serviceClass == null ||
      watchedValues.loadDuration == null ||
      watchedValues.tipTolerance == null ||
      watchedValues.existingMaterial == null ||
      watchedValues.existingBase == null ||
      watchedValues.existingHeight == null ||
      watchedValues.reinforcementMaterial == null ||
      watchedValues.reinforcementBase == null ||
      watchedValues.reinforcementHeight == null ||
      watchedValues.reinforcementSides == null ||
      watchedValues.permanentLoad == null ||
      watchedValues.liveLoad == null ||
      watchedValues.beamLength == null ||
      watchedValues.screw == null ||
      watchedValues.screwSpacing == null
    ) {
      return null;
    }

    // useWatch's DeepPartial return type doesn't narrow nested object fields even after
    // the null checks above, so assert the shape RHF actually guarantees at runtime.
    const existingMaterial = watchedValues.existingMaterial as TimberClass;
    const reinforcementMaterial = watchedValues.reinforcementMaterial as TimberClass;
    const screw = watchedValues.screw as Screw;

    return computeBeamReinforcement({
      serviceClass: watchedValues.serviceClass,
      loadDuration: watchedValues.loadDuration,
      tipTolerance: watchedValues.tipTolerance,
      existingMaterial,
      existingBase: watchedValues.existingBase,
      existingHeight: watchedValues.existingHeight,
      reinforcementMaterial,
      reinforcementBase: watchedValues.reinforcementBase,
      reinforcementHeight: watchedValues.reinforcementHeight,
      reinforcementSides: watchedValues.reinforcementSides,
      permanentLoad: watchedValues.permanentLoad,
      liveLoad: watchedValues.liveLoad,
      beamLength: watchedValues.beamLength,
      screw,
      screwSpacing: watchedValues.screwSpacing,
      preDrilled: watchedValues.preDrilled ?? false,
    });
  }, [watchedValues]);

  const formErrors = formMethods.formState.errors;
  const formTabs: Tab[] = [
    {
      title: i18n.translation.beamReinforcementModule.tabs.generalData,
      hasError: hasTabValidationError(GENERAL_TAB_FIELDS, formErrors),
    },
    {
      title: i18n.translation.beamReinforcementModule.tabs.beams,
      hasError: hasTabValidationError(BEAMS_TAB_FIELDS, formErrors),
    },
    {
      title: i18n.translation.beamReinforcementModule.tabs.screw,
      hasError: hasTabValidationError(SCREW_TAB_FIELDS, formErrors),
    },
  ];

  return (
    <FormProvider {...formMethods}>
      <FlexContent padding noPaddingTop className={styles.pageFlex}>
        <div className={styles.workspace} style={{ "--accent-base": accentColor } as CSSProperties}>
          <Card style={{ minHeight: 400 }} className={styles.formCard} accentColor="var(--accent-base, var(--red))" shadow>
            <Tabs renderMode="eager"
              urlParameter={BEAM_REINFORCEMENT_TAB_PARAMETER}
              tabs={formTabs}
              contents={[
                <GeneralDataCard defaultValues={cardDefaultValues} disabled={readOnly} />,
                <BeamsCard defaultValues={cardDefaultValues} timbers={timbers} disabled={readOnly} />,
                <ScrewCard defaultValues={cardDefaultValues} screws={screws} result={result} disabled={readOnly} />,
              ]}
            />
          </Card>

          <div className={styles.previewColumn}>
            <SectionDrawingCard
              className={styles.drawingCard}
              result={result}
              existingLength={watchedValues.existingLength}
              reinforcementLength={watchedValues.reinforcementLength}
            />

            <div className={styles.resultsPanel}>
              <ResultsCard result={result} />
            </div>
          </div>
        </div>
      </FlexContent>
    </FormProvider>
  );
};

export default BeamReinforcement;
